import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Users, Trash2, CalendarCheck, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

/**
 * RosterBonoUploader — gestión de rosters por trimestre para el bono.
 *
 * Lógica de fallback (backend):
 *   1. Snapshot propio del trimestre  → lo usa
 *   2. Snapshot más reciente anterior → lo hereda (carry-forward)
 *   3. Roster base (colección plana)  → fallback último recurso
 *
 * UI: tabs Q1/Q2/Q3/Q4 · cada tab muestra el roster del trimestre y permite
 * subir/reemplazar/vaciar independientemente. El roster base se puede
 * actualizar desde la sección inferior.
 */

const cfg = { withCredentials: true };

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const QUARTER_MONTHS = {
  Q1: 'Ene · Feb · Mar',
  Q2: 'Abr · May · Jun',
  Q3: 'Jul · Ago · Sep',
  Q4: 'Oct · Nov · Dic',
};

function getCurrentQuarter() {
  const m = new Date().getMonth() + 1;
  if (m <= 3) return 'Q1';
  if (m <= 6) return 'Q2';
  if (m <= 9) return 'Q3';
  return 'Q4';
}

function getCurrentYear() {
  return new Date().getFullYear();
}

/** Find the most recent quarterly snapshot key strictly before `q_key` from the snapshots map. */
function findCarryForwardSource(snapshotsMap, q_key) {
  const before = Object.keys(snapshotsMap)
    .filter((k) => k < q_key)
    .sort()
    .reverse();
  return before.length > 0 ? before[0] : null;
}

export const RosterBonoUploader = ({ open, onClose, onChanged }) => {
  const [year] = useState(getCurrentYear);
  const [selectedQ, setSelectedQ] = useState(getCurrentQuarter);

  // Map of quarter_key → snapshot metadata (no entries)
  const [snapshotsMap, setSnapshotsMap] = useState({});
  const [flatCount, setFlatCount] = useState(0);

  // Roster entries displayed for selected quarter
  const [displayRoster, setDisplayRoster] = useState([]);
  const [displaySource, setDisplaySource] = useState(''); // 'snapshot' | 'carry_forward' | 'base'
  const [displaySourceKey, setDisplaySourceKey] = useState(''); // the actual q_key data came from

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const quarterFileRef = useRef(null);
  const baseFileRef = useRef(null);

  // ── Load overview (snapshot metadata + flat count) ──────────────────────
  const loadOverview = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/dashboard/roster-bono/quarterly', cfg);
      const map = {};
      for (const s of data.snapshots || []) {
        map[s.quarter_key] = s;
      }
      setSnapshotsMap(map);
      setFlatCount(data.flat_roster_count || 0);
      return map;
    } catch {
      return {};
    }
  }, []);

  // ── Load roster entries for a specific quarter ───────────────────────────
  const loadQuarterRoster = useCallback(async (q, yr, map) => {
    const q_key = `${yr}-${q}`;
    setLoading(true);
    setErr('');
    try {
      if (map[q_key]) {
        // Has own snapshot
        const { data } = await axios.get(`/api/dashboard/roster-bono/quarterly/${q_key}`, cfg);
        setDisplayRoster(data.entries || []);
        setDisplaySource('snapshot');
        setDisplaySourceKey(q_key);
        return;
      }

      // Try carry-forward (most recent previous quarterly snapshot)
      const prevKey = findCarryForwardSource(map, q_key);
      if (prevKey) {
        const { data } = await axios.get(`/api/dashboard/roster-bono/quarterly/${prevKey}`, cfg);
        setDisplayRoster(data.entries || []);
        setDisplaySource('carry_forward');
        setDisplaySourceKey(prevKey);
        return;
      }

      // Fall back to flat base roster
      const { data } = await axios.get('/api/dashboard/roster-bono', cfg);
      setDisplayRoster(data.roster || []);
      setDisplaySource('base');
      setDisplaySourceKey('base');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo cargar el roster.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── On open: load everything ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    (async () => {
      const map = await loadOverview();
      await loadQuarterRoster(selectedQ, year, map);
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── On quarter change: re-load roster ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    loadQuarterRoster(selectedQ, year, snapshotsMap);
  }, [selectedQ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Upload to a specific quarter ─────────────────────────────────────────
  const handleQuarterUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('quarter', selectedQ);
      fd.append('year', String(year));
      const { data } = await axios.post('/api/dashboard/roster-bono/upload', fd, {
        ...cfg,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const map = await loadOverview();
      await loadQuarterRoster(selectedQ, year, map);
      onChanged && onChanged(data.count || 0);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Fallo al subir el Excel.');
    } finally {
      setLoading(false);
      if (quarterFileRef.current) quarterFileRef.current.value = '';
    }
  };

  // ── Clear a specific quarter snapshot ────────────────────────────────────
  const handleClearQuarter = async () => {
    const q_key = `${year}-${selectedQ}`;
    if (!snapshotsMap[q_key]) return;
    if (!window.confirm(`¿Eliminar el snapshot de ${selectedQ} ${year}? El trimestre volverá a usar el roster anterior o el roster base.`)) return;
    setLoading(true);
    setErr('');
    try {
      await axios.delete(`/api/dashboard/roster-bono/quarterly/${q_key}`, cfg);
      const map = await loadOverview();
      await loadQuarterRoster(selectedQ, year, map);
      onChanged && onChanged(0);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Fallo al vaciar.');
    } finally { setLoading(false); }
  };

  // ── Upload to flat base roster (legacy) ──────────────────────────────────
  const handleBaseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      // No quarter param → legacy flat mode
      const { data } = await axios.post('/api/dashboard/roster-bono/upload', fd, {
        ...cfg,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const map = await loadOverview();
      await loadQuarterRoster(selectedQ, year, map);
      onChanged && onChanged(data.count || 0);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Fallo al subir el Excel base.');
    } finally {
      setLoading(false);
      if (baseFileRef.current) baseFileRef.current.value = '';
    }
  };

  const handleClearBase = async () => {
    if (!window.confirm('¿Vaciar el roster base? Los trimestres sin snapshot propio quedarán sin roster.')) return;
    setLoading(true);
    try {
      await axios.delete('/api/dashboard/roster-bono', cfg);
      const map = await loadOverview();
      await loadQuarterRoster(selectedQ, year, map);
      onChanged && onChanged(0);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Fallo al vaciar.');
    } finally { setLoading(false); }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const qKey = `${year}-${selectedQ}`;
  const hasOwnSnapshot = !!snapshotsMap[qKey];
  const currentQ = getCurrentQuarter();

  function getTabStatus(q) {
    const k = `${year}-${q}`;
    if (snapshotsMap[k]) return { label: `${snapshotsMap[k].count}`, color: 'text-green-400' };
    const prevKey = findCarryForwardSource(snapshotsMap, k);
    if (prevKey) return { label: `← ${prevKey.split('-')[1]}`, color: 'text-yellow-500' };
    if (flatCount > 0) return { label: 'base', color: 'text-gray-500' };
    return { label: '—', color: 'text-gray-600' };
  }

  function getSourceBadge() {
    if (displaySource === 'snapshot') {
      const snap = snapshotsMap[qKey];
      const date = snap?.uploaded_at ? new Date(snap.uploaded_at).toLocaleDateString('es-AR') : '';
      return <span className="text-xs text-green-400">Snapshot propio{date ? ` · ${date}` : ''}</span>;
    }
    if (displaySource === 'carry_forward') {
      return <span className="text-xs text-yellow-500">Hereda de {displaySourceKey} · {displayRoster.length} perfiles</span>;
    }
    return <span className="text-xs text-gray-500">Roster base · {flatCount} perfiles</span>;
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="border-gray-800 text-white max-w-2xl"
        style={{ backgroundColor: '#0f0f0f' }}
        data-testid="roster-bono-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Users size={18} />
            Roster para cálculo del bono trimestral
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-gray-400 mb-4">
          Cada trimestre puede tener su propio roster. Si un trimestre no tiene snapshot propio,
          hereda el último subido. Columna requerida: <b>Nombre</b>. Opcional: <b>Fecha de alta</b>.
        </p>

        {/* Quarter tabs */}
        <div className="flex gap-1.5 mb-4">
          {QUARTERS.map((q) => {
            const k = `${year}-${q}`;
            const isSelected = selectedQ === q;
            const isCurrent = q === currentQ;
            const status = getTabStatus(q);
            return (
              <button
                key={q}
                onClick={() => setSelectedQ(q)}
                data-testid={`roster-tab-${q}`}
                className={`flex-1 py-2 px-1 rounded text-xs font-mono transition-all border ${
                  isSelected
                    ? 'bg-violet-700 border-violet-500 text-white'
                    : 'bg-gray-900 border-gray-800 text-gray-400 hover:bg-gray-800'
                }`}
              >
                <div className="font-semibold">{q}</div>
                <div className="text-[9px] mt-0.5">{QUARTER_MONTHS[q].split(' · ')[0]}</div>
                <div className={`text-[9px] font-mono mt-0.5 ${status.color}`}>{status.label}</div>
                {isCurrent && (
                  <div className="text-[8px] mt-0.5 text-blue-400">actual</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected quarter panel */}
        <div className="border border-gray-700 rounded-lg p-3 mb-4 bg-gray-900/40">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CalendarCheck size={14} className="text-violet-400" />
              <span className="text-sm font-semibold text-white">
                {selectedQ} {year} · {QUARTER_MONTHS[selectedQ]}
              </span>
            </div>
            <div>{getSourceBadge()}</div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={quarterFileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleQuarterUpload}
              className="hidden"
              data-testid="roster-bono-file-input"
            />
            <Button
              size="sm"
              onClick={() => quarterFileRef.current?.click()}
              disabled={loading}
              className="text-black font-semibold"
              style={{ backgroundColor: '#7C3AED' }}
              data-testid="roster-bono-upload-btn"
            >
              <Upload size={13} className="mr-1" />
              {hasOwnSnapshot ? `Reemplazar ${selectedQ}` : `+ Subir Excel para ${selectedQ}`}
            </Button>
            {hasOwnSnapshot && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleClearQuarter}
                disabled={loading}
                className="border-red-800 text-red-300 hover:bg-red-950/40"
                data-testid="roster-bono-clear-quarter-btn"
              >
                <Trash2 size={13} className="mr-1" /> Vaciar {selectedQ}
              </Button>
            )}
            <span className="text-xs text-gray-400 ml-auto" data-testid="roster-bono-count">
              {displayRoster.length} perfiles
            </span>
          </div>
        </div>

        {err && <p className="text-red-400 text-sm mb-3">{err}</p>}

        {/* Roster table */}
        <div className="max-h-64 overflow-y-auto border border-gray-800 rounded-lg mb-4">
          <table className="w-full text-xs">
            <thead className="bg-gray-900/60 sticky top-0">
              <tr className="text-gray-400 uppercase tracking-wider">
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-left">Fecha de alta</th>
                <th className="px-3 py-2 text-left">Clave match</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="3" className="px-3 py-4 text-center text-gray-500">Cargando…</td></tr>
              )}
              {!loading && displayRoster.length === 0 && (
                <tr><td colSpan="3" className="px-3 py-6 text-center text-gray-500">
                  Sin perfiles — subí un Excel para comenzar.
                </td></tr>
              )}
              {!loading && displayRoster.map((r, i) => (
                <tr key={i} className="border-t border-gray-800 hover:bg-gray-900/40">
                  <td className="px-3 py-2 text-white">{r.name}</td>
                  <td className="px-3 py-2 text-gray-300">{r.active_since || <span className="text-gray-600">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-gray-500">{r.match_key}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Base roster section */}
        <div className="border border-gray-800 rounded-lg p-3 bg-gray-950/60">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ChevronRight size={13} className="text-gray-500" />
              <span className="text-xs text-gray-400 font-semibold">Roster base (fallback global)</span>
              <span className="text-xs text-gray-600">{flatCount} perfiles</span>
            </div>
          </div>
          <p className="text-[10px] text-gray-600 mb-2">
            Usado por los trimestres sin snapshot propio ni snapshot anterior disponible.
          </p>
          <div className="flex gap-2">
            <input
              ref={baseFileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleBaseUpload}
              className="hidden"
              data-testid="roster-bono-base-file-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => baseFileRef.current?.click()}
              disabled={loading}
              className="border-gray-700 text-gray-300 hover:bg-gray-800 text-xs"
              data-testid="roster-bono-base-upload-btn"
            >
              <Upload size={12} className="mr-1" /> Reemplazar roster base
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearBase}
              disabled={loading || flatCount === 0}
              className="border-red-900/50 text-red-400/60 hover:bg-red-950/30 text-xs"
              data-testid="roster-bono-clear-btn"
            >
              <Trash2 size={12} className="mr-1" /> Vaciar base
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RosterBonoUploader;
